"use client";

import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  INPUT_MONO,
  SELECT,
} from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";

/*
 * Rows of "operator, value ± uncertainty" for the uncertainty calculator's sum and
 * product modes. Layout and behaviour only: every label arrives through `labels`.
 */

export interface MeasurementRow {
  id: number;
  op: string;
  value: string;
  uncertainty: string;
}

export interface MeasurementLabels {
  /** "Measurement {n}" */
  row: string;
  /** "Operation for measurement {n}" */
  operation: string;
  value: string;
  uncertainty: string;
  /** "Remove measurement {n}" */
  remove: string;
  add: string;
}

const MAX_ROWS = 10;

export function MeasurementRows({
  rows,
  onChange,
  ops,
  idPrefix,
  labels,
  nextId,
}: {
  rows: MeasurementRow[];
  onChange: (rows: MeasurementRow[]) => void;
  ops: readonly { value: string; label: string }[];
  idPrefix: string;
  labels: MeasurementLabels;
  nextId: () => number;
}) {
  const patch = (id: number, change: Partial<MeasurementRow>) =>
    onChange(rows.map((row) => (row.id === id ? { ...row, ...change } : row)));
  return (
    <div className="space-y-3">
      {rows.map((row, index) => {
        const n = index + 1;
        const base = `${idPrefix}-${row.id}`;
        return (
          <fieldset
            className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3"
            key={row.id}
          >
            <legend className={`${HINT} px-1`}>{t(labels.row, { n })}</legend>
            <div className="space-y-1">
              <label className="sr-only" htmlFor={`${base}-op`}>
                {t(labels.operation, { n })}
              </label>
              <select
                className={`${SELECT} w-16`}
                id={`${base}-op`}
                onChange={(event) => patch(row.id, { op: event.target.value })}
                value={row.op}
              >
                {ops.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[7rem] flex-1 space-y-1">
              <label className={HINT} htmlFor={`${base}-value`}>
                {labels.value}
              </label>
              <input
                className={INPUT_MONO}
                id={`${base}-value`}
                inputMode="decimal"
                onChange={(event) =>
                  patch(row.id, { value: event.target.value })
                }
                value={row.value}
              />
            </div>
            <div className="min-w-[7rem] flex-1 space-y-1">
              <label className={HINT} htmlFor={`${base}-unc`}>
                {labels.uncertainty}
              </label>
              <input
                className={INPUT_MONO}
                id={`${base}-unc`}
                inputMode="decimal"
                onChange={(event) =>
                  patch(row.id, { uncertainty: event.target.value })
                }
                value={row.uncertainty}
              />
            </div>
            <button
              aria-label={t(labels.remove, { n })}
              className={BUTTON_SMALL}
              disabled={rows.length <= 1}
              onClick={() => onChange(rows.filter((r) => r.id !== row.id))}
              type="button"
            >
              ×
            </button>
          </fieldset>
        );
      })}
      <button
        className={BUTTON_SECONDARY}
        disabled={rows.length >= MAX_ROWS}
        onClick={() =>
          onChange([
            ...rows,
            { id: nextId(), op: ops[0].value, value: "", uncertainty: "" },
          ])
        }
        type="button"
      >
        {labels.add}
      </button>
    </div>
  );
}
